import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Select, Input } from "antd";
import styled from "styled-components";
import { Create } from "@mui/icons-material";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import useSet from "../../../hooks/common/useSet";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { API_QUERY_CUSTOMER } from "../../../utils/apis";
import { WEB_CHANNEL } from "../../../utils/constants";

const { Option } = Select;

const UTILITY_TYPES = ["NWSC", "URA"] as const;

const CUSTOMER_AREAS = [
  "Kampala",
  "Jinja",
  "Entebbe",
  "Mukono",
  "Iganga",
  "Lugazi",
  "Kawuku",
  "Kajjansi",
  "Others",
] as const;

const CUSTOMER_TYPE = {
  AGENT: "AGENT",
  MERCHANT: "MERCHANT",
} as const;

const CUSTOMER_PREFIX = {
  AGENT: "YAGT",
  MERCHANT: "YMRT",
} as const;

interface FormState {
  paycode?: string;
  customer_ref?: string;
  customer_area?: string;
  amount?: number;
  phone_number?: string;
}

const UtilityPaymentRequest = () => {
  const { error, uploadData, success, submitting, data } = useSet();
  const location = useLocation();
  const restoredFormData = location.state?.formData || {};
  const [form, setForm] = useState<FormState>(restoredFormData);
  const navigate = useNavigate();
  const { tranToken, userNumber } = useAuth();

  const isFormValid = () => {
    if (!form.paycode || !form.customer_ref) return false;

    const customerRefStr = String(form.customer_ref);
    if (form.paycode === "URA" && customerRefStr.length !== 13) {
      return false;
    }
    if (form.paycode === "NWSC") {
      if (customerRefStr.length !== 8) return false;
      // Validate phone number: must be at least 10 digits (country code + number)
      const phoneNumber = form.phone_number?.replace(/\D/g, "") || "";
      if (!phoneNumber || phoneNumber.length < 10) return false;
      return !!(form.amount && form.customer_area);
    }

    return true;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "customer_ref" && value) {
      const maxLength =
        form.paycode === "URA" ? 13 : form.paycode === "NWSC" ? 8 : undefined;
      if (maxLength && value.length > maxLength) {
        return;
      }
    }

    setForm((prev) => ({
      ...prev,
      [name]: name === "amount" ? Number(value) : value,
    }));
  };

  const handleSelectChange = (value: string) => {
    setForm({
      paycode: value,
      customer_ref: "",
      amount: 0,
      customer_area: "",
      phone_number: "",
    });
  };

  const handleAreaChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      customer_area: value,
    }));
  };

  const handlePhoneChange = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "").slice(0, 12); // 256 + 9 digits max
    setForm((prev) => ({
      ...prev,
      phone_number: cleanedValue,
    }));
  };

  const getCustomerType = (userNum?: string): string => {
    if (!userNum) return "";
    if (userNum.startsWith(CUSTOMER_PREFIX.AGENT)) return CUSTOMER_TYPE.AGENT;
    if (userNum.startsWith(CUSTOMER_PREFIX.MERCHANT))
      return CUSTOMER_TYPE.MERCHANT;
    return "";
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const payload: any = {
      paycode: form.paycode,
      customer_ref: form.customer_ref ? String(form.customer_ref) : undefined,
      tran_token: tranToken,
      user_number: userNumber,
      customer_type: getCustomerType(userNumber),
      channel: WEB_CHANNEL,
    };

    if (form.paycode === "NWSC") {
      payload.customer_area = form.customer_area;
      payload.amount = Number(form.amount);
      payload.phone_number = form.phone_number;
    }

    uploadData({
      api: API_QUERY_CUSTOMER,
      params: payload,
      method: "POST",
    });
  };

  useEffect(() => {
    if (success) {
      navigate("/utility-payments/confirm", {
        state: {
          data,
          formData: form,
        },
      });
    }
  }, [success, navigate, data, form]);

  useEffect(() => {
    if (error && Object.keys(error).length > 0) {
      if (error?.status === "FAILED") {
        toast.error(`Payment failed. Please try again.`);
      } else {
        toast.error("An error occurred. Please try again.");
      }
    }
  }, [error]);

  return (
    <form onSubmit={onSubmit}>
      <Wrapper className="flexColumn gap20">
        <TitleWrapper className="flexNullCenter gap10 x100 whiteBg radius10">
          <Create fontSize="small" color="primary" />
          <p
            className="capitalize"
            style={{
              fontWeight: "bold",
              marginBottom: 0,
            }}
          >
            Pay with OnePayX
          </p>
        </TitleWrapper>
        <div className="whiteBg radius10 p-3">
          <div className="capitalize bold pb-1 flexNullCenter mainColor">
            Utility Payments form
          </div>
          <FieldsWrapper>
            <div>
              <label htmlFor="paycode">Utility Type</label>
              <Select
                id="paycode"
                placeholder="Select utility type"
                value={form.paycode}
                onChange={handleSelectChange}
                style={{ width: "100%" }}
              >
                {UTILITY_TYPES.map((item) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>
            </div>

            {form.paycode && (
              <div>
                <label htmlFor="customer_ref">
                  {form.paycode === "URA"
                    ? "PRN"
                    : form.paycode === "NWSC"
                      ? "Meter Number"
                      : "Customer Reference"}
                </label>
                <Input
                  id="customer_ref"
                  name="customer_ref"
                  type="number"
                  value={form.customer_ref ?? ""}
                  onChange={handleChange}
                  maxLength={
                    form.paycode === "URA"
                      ? 13
                      : form.paycode === "NWSC"
                        ? 8
                        : undefined
                  }
                  required
                />
              </div>
            )}

            {form.paycode === "NWSC" && (
              <>
                <div>
                  <label htmlFor="amount">Amount</label>
                  <Input
                    id="amount"
                    name="amount"
                    type="number"
                    value={form.amount ?? 0}
                    placeholder="Enter amount"
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="customer_area">Customer Area</label>
                  <Select
                    id="customer_area"
                    placeholder="Select area"
                    value={form.customer_area}
                    onChange={handleAreaChange}
                    style={{ width: "100%" }}
                  >
                    {CUSTOMER_AREAS.map((area) => (
                      <Option key={area} value={area}>
                        {area}
                      </Option>
                    ))}
                  </Select>
                </div>

                <div>
                  <label htmlFor="phone_number">Phone Number</label>
                  <PhoneInput
                    country={"ug"}
                    specialLabel={""}
                    value={form.phone_number || ""}
                    onChange={handlePhoneChange}
                    placeholder="Enter phone number"
                    enableSearch={true}
                    containerStyle={{ width: "100%" }}
                    inputClass="phone-input-custom"
                    dropdownStyle={{
                      borderRadius: "8px",
                      borderColor: "#ccc",
                      boxShadow:
                        "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
                    }}
                    disableDropdown={true}
                    countryCodeEditable={false}
                  />
                </div>
              </>
            )}
          </FieldsWrapper>
        </div>
        <ButtonWrapper>
          <Button
            type="primary"
            htmlType="submit"
            disabled={submitting || !isFormValid()}
            loading={submitting}
          >
            {submitting ? "Processing..." : "Continue"}
          </Button>
        </ButtonWrapper>
      </Wrapper>
    </form>
  );
};

export default UtilityPaymentRequest;

const Wrapper = styled.div`
  padding: 0 20px 20px 20px;
`;

const TitleWrapper = styled.div`
  padding: 10px;
`;

const FieldsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  font-size: 12px;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;
